// CORE-496 — the onboarding tour, spoken by Agapella inside her pop-up.
//
// Product decision: every onboarding interaction comes FROM the AI assistant,
// through the same pop-up she lives in the rest of the time — not a separate
// tour card. So this renders like her chat: her avatar, her message bubble, and
// the step's two options as reply chips. The spotlight overlay behind the page
// only dims and rings the block she is talking about.
//
// Presentation only — copy, progression and persistence live in
// OnboardingTourProvider.
import React from 'react';
import { Image, Pressable, ScrollView, StyleSheet, Text, View } from 'react-native';

import { useTheme } from '../context/ThemeContext';
import { AGAPELLA_AI_ICON } from '../constants/aiCompanions';
import { useOnboardingTour } from '../onboarding/OnboardingTourProvider';
import { primaryIntent, secondaryIntent, TourIntent } from '../onboarding/tourActions';
import { typographyTokens } from '../styles/typographyTokens';

/** Her message bubble — same shape as an incoming message in the chat thread. */
const Bubble: React.FC<{ text: string; title?: string }> = ({ text, title }) => {
  const { colors } = useTheme();
  return (
    <View style={styles.row}>
      <Image source={AGAPELLA_AI_ICON} style={styles.avatar} />
      {/* surfaceWarm is the chat thread's incoming-bubble token — same look as
          the messages she sends outside the tour. */}
      <View style={[styles.bubble, { backgroundColor: colors.surfaceWarm, borderColor: colors.borderSubtle }]}>
        {!!title && <Text style={[styles.title, { color: colors.textPrimary }]}>{title}</Text>}
        <Text style={[styles.text, { color: colors.textSecondary }]}>{text}</Text>
      </View>
    </View>
  );
};

export const AgapellaTourPanel: React.FC<{
  /**
   * CORE-1750 — «Перейти в чат с Agapella» на последнем шаге. Без него — как на
   * сайте: `goToChat` уводит в «Чаты». В приложении чат с ней и есть шторка, в
   * которой идёт тур, поэтому шторка передаёт свой ход: завершить тур и остаться
   * в ней уже с чатом.
   */
  onGoToChat?: () => void;
}> = ({ onGoToChat }) => {
  const tour = useOnboardingTour();
  const { colors } = useTheme();

  const step = tour?.step ?? null;
  if (!tour || !step) return null;

  const transition = tour.transitionText;

  const run = (intent: TourIntent) => {
    switch (intent) {
      case 'next': return tour.next();
      case 'complete': return tour.complete();
      case 'chat': return onGoToChat ? onGoToChat() : tour.goToChat();
      case 'skip_step': return tour.skipStep();
      case 'skip_all': return tour.skipAll();
      case 'close': return tour.close();
    }
  };

  return (
    <View style={styles.root} testID="agapella-tour-panel">
      <ScrollView style={styles.scroll} contentContainerStyle={styles.scrollContent}>
        <Text style={[styles.progress, { color: colors.textMuted }]} testID="agapella-tour-progress">
          Знакомство · шаг {step.id} из {tour.totalSteps}
        </Text>

        {/* The step itself. Once she's answering a skip, her reply follows it. */}
        <Bubble title={step.title} text={step.body} />
        {!!transition && <Bubble text={transition} />}
      </ScrollView>

      {/* Options — hidden while she is answering a skip and moving on herself. */}
      {!transition && (
        <View style={[styles.actions, { borderTopColor: colors.borderSubtle }]}>
          <Pressable
            onPress={() => run(primaryIntent(step))}
            accessibilityRole="button"
            testID="agapella-tour-primary"
            style={[styles.primaryBtn, { backgroundColor: colors.solar }]}
          >
            <Text style={styles.primaryLabel}>{step.primaryLabel}</Text>
          </Pressable>
          <Pressable
            onPress={() => run(secondaryIntent(step))}
            accessibilityRole="button"
            testID="agapella-tour-secondary"
            style={[styles.secondaryBtn, { borderColor: colors.borderStrong }]}
          >
            <Text style={[styles.secondaryLabel, { color: colors.textMuted }]}>
              {step.secondaryLabel}
            </Text>
          </Pressable>
        </View>
      )}
    </View>
  );
};

/**
 * Her parting line after a skip/close — the pop-up's last frame before it
 * collapses into the launcher icon.
 */
export const AgapellaFarewellPanel: React.FC<{ text: string }> = ({ text }) => (
  <View style={styles.root} testID="agapella-tour-farewell">
    <ScrollView style={styles.scroll} contentContainerStyle={styles.scrollContent}>
      <Bubble text={text} />
    </ScrollView>
  </View>
);

const styles = StyleSheet.create({
  root: { flex: 1 },
  scroll: { flex: 1 },
  scrollContent: { padding: 14, gap: 12 },
  progress: { ...typographyTokens.captionAccent },
  row: { flexDirection: 'row', alignItems: 'flex-start', gap: 8 },
  avatar: { width: 28, height: 28, borderRadius: 14, marginTop: 2 },
  bubble: {
    flex: 1,
    borderRadius: 16,
    borderTopLeftRadius: 4,
    borderWidth: StyleSheet.hairlineWidth,
    paddingHorizontal: 14,
    paddingVertical: 12,
    gap: 6,
  },
  title: { ...typographyTokens.bodyAccent },
  text: { ...typographyTokens.body },
  actions: { padding: 12, gap: 8, borderTopWidth: StyleSheet.hairlineWidth },
  primaryBtn: { paddingVertical: 12, borderRadius: 12, alignItems: 'center' },
  primaryLabel: { ...typographyTokens.buttonPrimary, color: '#1A1206' },
  secondaryBtn: {
    paddingVertical: 11,
    borderRadius: 12,
    alignItems: 'center',
    borderWidth: StyleSheet.hairlineWidth,
  },
  secondaryLabel: { ...typographyTokens.buttonSecondary },
});
